import { useCallback, useMemo, useEffect } from 'react';

const API_BASE = 'https://musicbrainz.org/ws/2';
const RATE_LIMIT = 1000; // MusicBrainz: average 1 request per second per IP
const MAX_RETRIES = 3;
const CACHE_EXPIRY = 24 * 60 * 60 * 1000;
const LOOKUP_INC = 'artist-rels+url-rels+aliases+genres';
const MAX_IDLE_PREFETCH = 12;
const CACHE_STORAGE_KEY = 'artistCache';
const SEARCH_PRIORITY = 2;
const USER_PRIORITY = 1;
const IDLE_PRIORITY = 0;

const lastRequestTime = { current: 0 };
const requestQueue = [];
const inflight = new Map();
let pumping = false;
let cache = {};
const listeners = new Set();
let prefetchGeneration = 0;
let warmingId = null;
let lastWarmedDetails = null;
let idleResumeTimer = null;
let activeJob = null;

try {
  const stored = localStorage.getItem(CACHE_STORAGE_KEY);
  if (stored) cache = JSON.parse(stored);
} catch (err) {
  cache = {};
}

const saveCache = () => {
  try {
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(cache));
  } catch (err) {
    console.warn('Artist cache could not be saved');
  }
};

const getCachedData = (key) => {
  const cachedItem = cache[key];
  if (cachedItem && Date.now() - cachedItem.timestamp < CACHE_EXPIRY) {
    return cachedItem.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  cache[key] = { data, timestamp: Date.now() };
  saveCache();
};

const notify = (artist) => {
  listeners.forEach((listener) => listener(artist));
};

const lookupUrl = (artistId) => (
  `${API_BASE}/artist/${artistId}?inc=${LOOKUP_INC}&fmt=json`
);

const isHydrated = (artist) => Array.isArray(artist?.relations);

const uniqueMembers = (details) => {
  const members = new Map();
  (details.relations || []).forEach((rel) => {
    if (rel.type !== 'member of band' && rel.type !== 'member') return;
    const memberId = rel.artist?.id;
    if (!memberId) return;
    if (!members.has(memberId)) {
      members.set(memberId, { ...rel, attributes: [...(rel.attributes || [])] });
      return;
    }
    const existing = members.get(memberId);
    existing.attributes = [...new Set([...(existing.attributes || []), ...(rel.attributes || [])])];
    if (rel.begin && (!existing.begin || new Date(rel.begin) < new Date(existing.begin))) {
      existing.begin = rel.begin;
    }
    if (rel.end && (!existing.end || new Date(rel.end) > new Date(existing.end))) {
      existing.end = rel.end;
    }
  });
  return members;
};

const mergeMember = (group, memberDetails) => ({
  ...group,
  relations: (group.relations || []).map((rel) => (
    rel.artist?.id === memberDetails.id ? { ...rel, artist: memberDetails } : rel
  )),
});

const relatedGroupIds = (details) => {
  const ids = new Set();
  (details.relations || []).forEach((rel) => {
    (rel.artist?.relations || []).forEach((inner) => {
      if (inner.type === 'member of band' && inner.artist?.id && inner.artist.id !== details.id) {
        ids.add(inner.artist.id);
      }
    });
  });
  return [...ids];
};

const attachCachedMembers = (details) => {
  const members = uniqueMembers(details);
  members.forEach((rel, memberId) => {
    const cachedArtist = getCachedData(`artist:${memberId}`);
    if (cachedArtist) {
      members.set(memberId, { ...rel, artist: cachedArtist });
    }
  });
  details.relations = Array.from(members.values());
  return details;
};

const isSearchUrl = (url) => url.includes('/artist?query=');

const isAbortError = (error) => error?.name === 'AbortError';

const abortJob = (job, reason = 'Replaced by a newer request') => {
  if (!job) return;
  try {
    job.controller?.abort(reason);
  } catch (err) {
    // Already aborted.
  }
  inflight.delete(job.url);
  const index = requestQueue.indexOf(job);
  if (index >= 0) requestQueue.splice(index, 1);
  job.reject(Object.assign(new Error(reason), { name: 'AbortError' }));
};

const dropIdleJobs = () => {
  [...requestQueue].forEach((job) => {
    if (job.priority === IDLE_PRIORITY) abortJob(job, 'Paused idle prefetch');
  });
  if (activeJob && activeJob.priority === IDLE_PRIORITY) {
    abortJob(activeJob, 'Paused idle prefetch');
    activeJob = null;
  }
};

const dropOtherSearches = (keepUrl) => {
  [...requestQueue].forEach((job) => {
    if (isSearchUrl(job.url) && job.url !== keepUrl) {
      abortJob(job, 'Replaced by a newer search');
    }
  });
  if (activeJob && isSearchUrl(activeJob.url) && activeJob.url !== keepUrl) {
    abortJob(activeJob, 'Replaced by a newer search');
    activeJob = null;
  }
};

const pauseIdleWarm = () => {
  prefetchGeneration += 1;
  dropIdleJobs();
  if (idleResumeTimer) {
    clearTimeout(idleResumeTimer);
    idleResumeTimer = null;
  }
};

const resumeIdleWarmSoon = () => {
  if (idleResumeTimer) clearTimeout(idleResumeTimer);
  idleResumeTimer = setTimeout(() => {
    idleResumeTimer = null;
    if (!lastWarmedDetails) return;
    prefetchGeneration += 1;
    warmingId = lastWarmedDetails.id;
    scheduleIdleWarm(lastWarmedDetails);
  }, 2500);
};

const pump = () => {
  if (pumping || requestQueue.length === 0) return;
  pumping = true;

  const wait = Math.max(0, RATE_LIMIT - (Date.now() - lastRequestTime.current));

  setTimeout(async () => {
    requestQueue.sort((a, b) => b.priority - a.priority);
    const job = requestQueue.find((item) => item.priority > 0)
      || (typeof document !== 'undefined' && document.hidden ? null : requestQueue[0]);

    if (!job) {
      pumping = false;
      setTimeout(pump, 800);
      return;
    }

    const index = requestQueue.indexOf(job);
    if (index >= 0) requestQueue.splice(index, 1);

    const controller = new AbortController();
    job.controller = controller;
    activeJob = job;
    lastRequestTime.current = Date.now();

    try {
      const response = await fetch(job.url, {
        signal: controller.signal,
        headers: {
          'User-Agent': 'BandBacon/1.0.0 ( https://bandbacon.com )',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (activeJob === job) activeJob = null;
      inflight.delete(job.url);
      job.resolve(data);
    } catch (error) {
      if (activeJob === job) activeJob = null;
      if (isAbortError(error)) {
        inflight.delete(job.url);
        pumping = false;
        pump();
        return;
      }
      if (job.retries < MAX_RETRIES) {
        job.retries += 1;
        job.controller = null;
        requestQueue.push(job);
      } else {
        inflight.delete(job.url);
        job.reject(error);
      }
    }

    pumping = false;
    pump();
  }, wait);
};

const enqueue = (url, priority = USER_PRIORITY) => {
  if (priority >= SEARCH_PRIORITY || isSearchUrl(url)) {
    pauseIdleWarm();
    dropOtherSearches(url);
    resumeIdleWarmSoon();
  } else if (priority >= USER_PRIORITY) {
    dropIdleJobs();
  }

  const existing = inflight.get(url);
  if (existing) {
    existing.priority = Math.max(existing.priority, priority);
    pump();
    return existing.promise;
  }

  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  const job = { url, resolve, reject, retries: 0, priority, promise, controller: null };
  inflight.set(url, job);
  requestQueue.push(job);
  pump();
  return promise;
};

const warmArtist = async (artistId, generation) => {
  if (generation !== prefetchGeneration) return;
  const cacheKey = `artist:${artistId}`;
  if (getCachedData(cacheKey)) return;

  try {
    const details = await enqueue(lookupUrl(artistId), IDLE_PRIORITY);
    if (generation !== prefetchGeneration) return;
    setCachedData(cacheKey, details);
  } catch (error) {
    if (isAbortError(error)) return;
    console.error('Idle prefetch failed for', artistId, error);
  }
};

const hydrateGroupMembers = async (groupId, generation) => {
  const cacheKey = `artist:${groupId}`;
  const group = getCachedData(cacheKey);
  if (!group || group.type !== 'Group') return;

  const members = uniqueMembers(group);
  for (const [memberId, rel] of members) {
    if (generation !== prefetchGeneration) return;

    const cachedMember = getCachedData(`artist:${memberId}`);
    if (cachedMember) {
      if (!isHydrated(rel.artist)) {
        const latest = getCachedData(cacheKey) || group;
        const merged = mergeMember(latest, cachedMember);
        setCachedData(cacheKey, merged);
        notify(merged);
      }
      continue;
    }

    try {
      const memberDetails = await enqueue(lookupUrl(memberId), IDLE_PRIORITY);
      if (generation !== prefetchGeneration) return;
      setCachedData(`artist:${memberId}`, memberDetails);
      const latest = getCachedData(cacheKey) || group;
      const merged = mergeMember(latest, memberDetails);
      setCachedData(cacheKey, merged);
      notify(merged);
    } catch (error) {
      if (isAbortError(error)) return;
      console.error('Member hydrate failed for', memberId, error);
    }
  }

  const latest = getCachedData(cacheKey);
  if (!latest) return;
  const neighbors = relatedGroupIds(latest).slice(0, MAX_IDLE_PREFETCH);
  for (const neighborId of neighbors) {
    if (generation !== prefetchGeneration) return;
    await warmArtist(neighborId, generation);
  }
};

const scheduleIdleWarm = (details) => {
  lastWarmedDetails = details;
  const generation = prefetchGeneration;
  if (details.type === 'Group') {
    hydrateGroupMembers(details.id, generation);
    return;
  }

  const bands = (details.relations || [])
    .filter((rel) => rel.type === 'member of band' && rel.artist?.id)
    .map((rel) => rel.artist.id)
    .slice(0, MAX_IDLE_PREFETCH);

  bands.reduce(async (previous, bandId) => {
    await previous;
    await warmArtist(bandId, generation);
  }, Promise.resolve());
};

const fetchArtistDetails = async (artistId) => {
  const cacheKey = `artist:${artistId}`;
  const cachedData = getCachedData(cacheKey);
  if (cachedData) {
    lastWarmedDetails = cachedData;
    if (warmingId !== artistId) {
      warmingId = artistId;
      resumeIdleWarmSoon();
    }
    return cachedData;
  }

  prefetchGeneration += 1;
  warmingId = artistId;
  const generation = prefetchGeneration;

  try {
    const details = await enqueue(lookupUrl(artistId), USER_PRIORITY);
    if (details.type === 'Group') {
      attachCachedMembers(details);
    }
    setCachedData(cacheKey, details);
    lastWarmedDetails = details;
    if (generation === prefetchGeneration) {
      resumeIdleWarmSoon();
    }
    return details;
  } catch (error) {
    console.error('Error fetching artist details:', error);
    return null;
  }
};

export function useMusicBrainz() {
  useEffect(() => {
    const resume = () => pump();
    document.addEventListener('visibilitychange', resume);
    return () => document.removeEventListener('visibilitychange', resume);
  }, []);

  const searchArtist = useCallback(async (term, type = null) => {
    const query = `artist:${term}${type ? ` AND type:${type}` : ''}`;
    const response = await enqueue(`${API_BASE}/artist?query=${encodeURIComponent(query)}&fmt=json`, SEARCH_PRIORITY);
    return response.artists || [];
  }, []);

  const searchArtistLight = useCallback(async (term, limit = 5) => {
    try {
      const searchData = await enqueue(
        `${API_BASE}/artist?query=${encodeURIComponent(term)}&fmt=json&limit=${limit}`,
        SEARCH_PRIORITY
      );
      if (searchData.artists && searchData.artists.length > 0) {
        return searchData.artists.map((artist) => ({
          id: artist.id,
          name: artist.name,
          disambiguation: artist.disambiguation || '',
          type: artist.type,
        }));
      }
      return [];
    } catch (error) {
      console.error('Error in searchArtistLight:', error);
      return [];
    }
  }, []);

  const getArtistDetails = useCallback((artistId) => fetchArtistDetails(artistId), []);

  const searchArtistWithDetails = useCallback(async (term, type = null) => {
    const searchResults = await searchArtist(term, type);
    if (searchResults.length > 0) {
      return fetchArtistDetails(searchResults[0].id);
    }
    return null;
  }, [searchArtist]);

  const subscribe = useCallback((listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  const clearCache = useCallback(() => {
    localStorage.removeItem(CACHE_STORAGE_KEY);
    cache = {};
  }, []);

  return useMemo(() => ({
    searchArtistWithDetails,
    searchArtistLight,
    getArtistDetails,
    clearCache,
    searchArtist,
    subscribe,
  }), [searchArtistWithDetails, searchArtistLight, getArtistDetails, clearCache, searchArtist, subscribe]);
}
